import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Check, X, Download, Share2, Loader2, Receipt } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { generatePDF } from "@/lib/pdfGenerator";
import { generateReceiptPDF } from "@/lib/receiptPdfGenerator";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

interface Product {
  id: string;
  name: string;
  code: string;
  unit: string;
}

interface OrderItem {
  quantity: number;
  product: {
    id: string;
    name: string;
    code: string;
    unit: string;
  };
}

interface Order {
  id: string;
  order_number: number;
  responsible_name: string;
  construction_name: string;
  status: string;
  created_at: string;
  department: {
    id: string;
    name: string;
    acronym: string;
  };
  order_items: OrderItem[];
  reject_reason?: string;
}

interface Department {
  id: string;
  name: string;
  acronym: string;
}

const statusMap = {
  pending: { label: "Pendente", color: "bg-yellow-100 text-yellow-800" },
  Approved: { label: "Aprovado", color: "bg-green-100 text-green-800" },
  Rejected: { label: "Rejeitado", color: "bg-red-100 text-red-800" },
  completed: { label: "Concluído", color: "bg-blue-100 text-blue-800" }
};

export default function AdminOrderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchOrderDetails = async () => {
    try {
      if (!id) {
        console.error('ID do pedido não fornecido');
        toast({
          title: "Erro",
          description: "ID do pedido não fornecido",
          variant: "destructive",
        });
        navigate('/orders/management');
        return;
      }

      // Primeiro, vamos verificar se o pedido existe
      const { data: orderExists, error: checkError } = await supabase
        .from('orders')
        .select('id')
        .eq('id', id)
        .single();

      if (checkError) {
        console.error('Erro ao verificar pedido:', checkError);
        throw checkError;
      }

      if (!orderExists) {
        console.error('Pedido não encontrado:', id);
        toast({
          title: "Erro",
          description: "Pedido não encontrado",
          variant: "destructive",
        });
        navigate('/orders/management');
        return;
      }

      // Agora vamos buscar todos os detalhes
      const { data: orderData, error } = await supabase
        .from('orders')
        .select(`
          *,
          department:departments (*),
          order_items (
            quantity,
            product:products (*)
          ),
          reject_reason
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Erro ao buscar pedido:', error);
        toast({
          title: "Erro",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      if (!orderData) {
        console.error('Dados do pedido não encontrados');
        toast({
          title: "Erro",
          description: "Dados do pedido não encontrados",
          variant: "destructive",
        });
        navigate('/orders/management');
        return;
      }

      // Formatar o pedido para seguir a interface
      const transformedOrder: Order = {
        ...orderData,
        department: {
          id: orderData.department.id,
          name: orderData.department.name,
          acronym: orderData.department.acronym,
        },
        order_items: orderData.order_items.map((item: any) => ({
          quantity: item.quantity,
          product: {
            id: item.product.id,
            name: item.product.name,
            code: item.product.code,
            unit: item.product.unit,
          },
        })),
        reject_reason: orderData.reject_reason
      };

      setOrder(transformedOrder);
      
    } catch (error: any) {
      console.error('Erro ao buscar detalhes do pedido:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível carregar os detalhes do pedido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center">
        <p>Pedido não encontrado</p>
      </div>
    );
  }

  const status = statusMap[order.status as keyof typeof statusMap] || statusMap.pending;

  const handleApprove = async () => {
    try {
      if (!order) return;

      const { error } = await supabase
        .from('orders')
        .update({ status: 'Approved' })
        .eq('id', order.id);

      if (error) throw error;

      setOrder(prev => prev ? { ...prev, status: 'Approved' } : null);

      toast({
        title: "Sucesso",
        description: "Pedido aprovado com sucesso",
      });
    } catch (error) {
      console.error('Erro ao aprovar pedido:', error);
      toast({
        title: "Erro",
        description: "Não foi possível aprovar o pedido",
        variant: "destructive",
      });
    }
  };

  const handleReject = async () => {
    try {
      if (!order) return;

      const { error } = await supabase
        .from('orders')
        .update({ status: 'Rejected' })
        .eq('id', order.id);

      if (error) throw error;

      setOrder(prev => prev ? { ...prev, status: 'Rejected' } : null);

      toast({
        title: "Sucesso",
        description: "Pedido rejeitado com sucesso",
      });
    } catch (error) {
      console.error('Erro ao rejeitar pedido:', error);
      toast({
        title: "Erro",
        description: "Não foi possível rejeitar o pedido",
        variant: "destructive",
      });
    }
  };

  const handleDownloadPDF = async () => {
    try {
      if (!order) return;

      const pdfBlob = await generatePDF(order);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedido-${order.order_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o PDF",
        variant: "destructive",
      });
    }
  };

  const handleDownloadReceipt = () => {
    if (!order) return;

    try {
      const doc = generateReceiptPDF(order);
      doc.save(`comprovante-pedido-${order.order_number}.pdf`);
      
      toast({
        title: "Sucesso",
        description: "Comprovante gerado com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao gerar comprovante:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o comprovante",
        variant: "destructive",
      });
    }
  };

  const handleShare = () => {
    if (!order) return;

    const text = `🏗️ *Pedido #${order.order_number}*\n\n` +
      `📅 Data: ${new Date(order.created_at).toLocaleDateString('pt-BR')}\n` +
      `👤 Responsável: ${order.responsible_name}\n` +
      `🏢 Departamento: ${order.department?.name}\n` +
      `🏗️ Obra: ${order.construction_name}\n` +
      `📊 Status: ${statusMap[order.status as keyof typeof statusMap].label}` +
      `${order.status === 'Rejected' && order.reject_reason ? `\n❌ Motivo da Rejeição: ${order.reject_reason}` : ''}\n\n` +
      `📦 *Itens do Pedido:*\n${order.order_items?.map(item => 
        `▫️ ${item.quantity}x ${item.product.unit} - ${item.product.name}`
      ).join('\n')}\n\n` +
      `Sistema GestockPro 🚀`;

    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encodedText}`, '_blank');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/orders/management')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h2 className="text-2xl font-bold">Pedido #{order.order_number}</h2>
          <Badge className={status.color}>
            {status.label}
          </Badge>
        </div>
        <div className="flex gap-2">
          {order.status === 'Approved' && (
            <Button variant="outline" onClick={handleDownloadReceipt}>
              <Receipt className="w-4 h-4 mr-2" />
              Gerar Comprovante
            </Button>
          )}
          <Button variant="outline" onClick={() => handleShare()}>
            <Share2 className="w-4 h-4 mr-2" />
            Compartilhar
          </Button>
          <Button variant="outline" onClick={handleDownloadPDF}>
            <Download className="w-4 h-4 mr-2" />
            Baixar PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Obra</CardTitle>
          </CardHeader>
          <CardContent>
            <p>{order.construction_name}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Informações do Pedido</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p>Número do Pedido</p>
              <p>{order.order_number}</p>
            </div>
            <div>
              <p>Status</p>
              <p>{status.label}</p>
            </div>
            <div>
              <p>Data do Pedido</p>
              <p>{new Date(order.created_at).toLocaleDateString('pt-BR')}</p>
            </div>
            <div>
              <p>Responsável</p>
              <p>{order.responsible_name}</p>
            </div>
            <div>
              <p>Departamento</p>
              <p>{order.department?.name}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Itens do Pedido</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Produto</TableHead>
                <TableHead>Quantidade</TableHead>
                <TableHead>Unidade</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order?.order_items && order.order_items.length > 0 ? (
                order.order_items.map((item) => (
                  <TableRow key={item.product.id}>
                    <TableCell>{item.product.code}</TableCell>
                    <TableCell>{item.product.name}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.product.unit}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">
                    Nenhum item encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {order.status === 'pending' && (
        <div className="flex justify-end gap-2">
          <Button variant="destructive" onClick={() => handleReject()}>
            <X className="w-4 h-4 mr-2" />
            Rejeitar
          </Button>
          <Button variant="default" onClick={() => handleApprove()}>
            <Check className="w-4 h-4 mr-2" />
            Aprovar
          </Button>
        </div>
      )}
    </div>
  );
}
